import { FileInfoDto, FileType, QueryFactory } from '@lmt/shared-types';
import { usePublishDocumentHandler } from '@lmt/unicorn-shared/app/components/documents/richedit/usePublishDocumentHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import React from 'react';
import {
  RibbonTabType,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { CustomButton } from './CustomButton';
import { useEventCallback } from '@mui/material';

export const printDocumentButtonId = 'printDocumentButtonId';

/*
  Component that adds "Print" button that publishes current draft version as PDF and prints it.
  Differs from ReadyForPdfButton in that it doesn't open the PDF in a new tab, but directly opens print dialog
 */
type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
  tab: RibbonTabType;
  patientId: number;
  dataBlockId: number | undefined;
  documentId: string | undefined;
  groupId: string | undefined;
  templateId: number | undefined | null;
  onNewDocumentId?: (newId: string) => void;
  versionHistory?: FileInfoDto[];
};

export const PublishAndPrintDocumentRichEditButton: React.FC<Props> = (
  props,
) => {
  const i18n = useScopedTranslation('documents');
  const { versionHistory } = props;

  const printPdf = useEventCallback(async (documentId: string) => {
    const iframe = document.createElement('iframe');
    iframe.hidden = true;

    const file = await QueryFactory.FileClient.downloadFile(documentId);
    // create object URL for your blob or file and set it as the iframe's src
    iframe.src = window.URL.createObjectURL(
      new Blob([file.data], { type: 'application/pdf' }),
    );

    // call the print method in the iframe onload handler
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    };
    document.body.appendChild(iframe);
  });

  const handlePublishAndPrint = usePublishDocumentHandler({
    ...props,
    richEdit: props.richEdit.current,
    afterPublishAction: (newVersion) => printPdf(newVersion.id),
  });

  const handleClick = useEventCallback(async () => {
    const richEdit = props.richEdit.current;

    // If there are no changes in editor and published version exists, we need to only print the published version
    if (richEdit && !richEdit.hasUnsavedChanges && versionHistory) {
      const publishedVersions = versionHistory.filter(
        (version) => !version.isDeleted && version.type === FileType.Document,
      );

      if (publishedVersions.length > 0) {
        const latestPublished = publishedVersions.reduce((prev, current) =>
          current.versionNumber > prev.versionNumber ? current : prev,
        );

        // Also we need to check that the draft was not updated later than the latest published version
        // Otherwise, we need to publish the draft
        const hasNewerDraft = versionHistory.some(
          (v) =>
            !v.isDeleted &&
            v.type === FileType.DocumentDraft &&
            v.uploadDateTime.getTime() -
              latestPublished.uploadDateTime.getTime() >
              1000,
        );

        if (!hasNewerDraft) {
          await printPdf(latestPublished.id);
          return;
        }
      }
    }

    // Otherwise, publish the draft and then print
    await handlePublishAndPrint();
  });

  return (
    <CustomButton
      {...props}
      buttonId={printDocumentButtonId}
      onClick={handleClick}
      text={i18n.t('publish_and_print.button')}
      icon={'dxre-icon-Print'}
    />
  );
};
